import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Breadcrumbs,
    List,
    LinearProgress,
    Grid,
    Card,
    CardMedia,
    CardContent,
    IconButton,
    Divider
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import VideoDialog from "../../common/VideoDialog";
import { db, auth } from "../../firebaseConfig";
import {
    collection,
    getDocs,
    query,
    where
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import { PlayCircleFilledRounded } from "@mui/icons-material";
import { formatDuration, formatRelativeDate, formatDate } from "../../utils/DateTimeUtils";
// import { setLogLevel } from "firebase/firestore";
// setLogLevel("debug");

const StudentVideoDashboard = () => {
    const [currentPath, setCurrentPath] = useState([]);
    const [assignedItems, setAssignedItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [openVideoDialog, setOpenVideoDialog] = useState(false);
    const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [renamedPath, setRenamedPath] = useState([]);

    const pathString = currentPath.join("/items/");

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                fetchAssignedItems(user.uid);
            }
        });

        return () => unsubscribe();
    }, [pathString]);

    const fetchAssignedItems = async (studentId) => {
        setLoading(true);
        try {


            const folderRef = collection(db, `videos/${pathString || "root"}/items`);

            /*  ⬇ add the security filter  */
            const q = query(folderRef, where("assignedStudents", "array-contains", studentId));

            const snapshot = await getDocs(q);

            const assignedVideos = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

            setAssignedItems(assignedVideos);
        } catch (error) {
            console.error("Failed to fetch assigned items:", error);
        } finally {
            setLoading(false);
        }
    };



    useEffect(() => {
        const filtered = assignedItems.filter((item) => {
            const itemPath = item.path || "";
            const currentPathStr = currentPath.join("/");
            return (
                (!currentPathStr && !itemPath) ||
                itemPath === currentPathStr ||
                itemPath.startsWith(currentPathStr + "/")
            );
        });
        setFilteredItems(filtered);
    }, [assignedItems, currentPath]);

    const handleVideoClick = (url) => {
        setSelectedVideoUrl(url);
        setOpenVideoDialog(true);
    };

    return (
        <Box p={3}>
            <Typography variant="h5" mb={2} color="primary">
                My Videos
            </Typography>

            <Breadcrumbs>
                <Typography onClick={() => {
                    setCurrentPath([])
                    setRenamedPath([])
                }} sx={{ cursor: "pointer" }}>
                    Root
                </Typography>


                {currentPath.map((p, idx) => (
                    <Typography
                        key={idx}
                        onClick={() => {
                            setCurrentPath(currentPath.slice(0, idx + 1))
                            setRenamedPath(renamedPath.slice(0, idx + 1))
                        }}
                        sx={{ cursor: "pointer" }}
                    >
                        {renamedPath[idx] || p}
                    </Typography>
                ))}
            </Breadcrumbs>

            {loading ? (
                <Box mt={2}>
                    <Typography>Loading...</Typography>
                    <LinearProgress />
                </Box>
            ) : (
                <List>
                    {assignedItems.length === 0 ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                            minHeight="300px"
                        >
                            <Typography variant="h6" color="textSecondary">
                                No content available
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2} marginTop={1}>
                                {/* ─────── Styled Folders ─────── */}
                                {assignedItems
                                    .filter(item => item.type === 'folder')
                                    .map(item => (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                                            <Card
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    p: 2,
                                                    cursor: 'pointer',
                                                    '&:hover': { boxShadow: 6, bgcolor: 'grey.100' }
                                                }}
                                                onClick={() => {
                                                    setRenamedPath([...renamedPath, item.displayName ? item.displayName : item.name])
                                                    setCurrentPath([...currentPath, item.name])
                                                }
                                                }
                                            >
                                                <FolderIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                                                <Typography variant="subtitle1" sx={{ ml: 2 }}>
                                                    {item.displayName ? item.displayName : item.name}
                                                </Typography>
                                            </Card>
                                        </Grid>
                                    ))}
                            </Grid>

                            {/* ─────── Divider ─────── */}
                            {assignedItems.some(item => item.type === 'folder') &&
                                assignedItems.some(item => item.type === 'video') && (
                                    <Divider sx={{ my: 3, borderColor: 'grey.400' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Videos
                                        </Typography>
                                    </Divider>
                                )}

                            <Grid container spacing={2}>
                                {assignedItems
                                    .filter(item => item.type === 'video')
                                    .map(item => (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={item.id} sx={{ marginBottom: 3 }}>
                                            <Card
                                                sx={{
                                                    maxWidth: 400,
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    transition: 'transform 0.3s',
                                                    '&:hover': {
                                                        transform: 'scale(1.02)',
                                                        cursor: 'pointer'
                                                    },
                                                    position: 'relative',
                                                }}
                                                onClick={() => handleVideoClick(item.url)}
                                            >
                                                {/* ─────── Thumbnail ─────── */}
                                                {item.thumbUrl ? (
                                                    <Box sx={{ position: 'relative' }}>
                                                        <CardMedia
                                                            component="img"
                                                            image={item.thumbUrl}
                                                            alt={item.displayName || item.name}
                                                            sx={{
                                                                width: '100%',
                                                                height: 200,
                                                                objectFit: 'fill',
                                                            }}
                                                            onError={e => {
                                                                e.currentTarget.src = '';
                                                            }}
                                                        />

                                                        {/* ─────── Duration Overlay ─────── */}
                                                        {item.duration && (
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    bottom: 8,
                                                                    right: 8,
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                                    color: '#fff',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '4px',
                                                                    fontSize: '12px',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {formatDuration(item.duration)}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            height: 200,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: 'grey.200',
                                                        }}
                                                    >
                                                        <PlayCircleFilledRounded fontSize="large" color="disabled" />
                                                        <Typography ml={1} color="text.secondary">
                                                            No Thumbnail
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Card>

                                            {/* ─────── Title ─────── */}
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.9rem',
                                                    padding: '9px 0 4px 0',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    color: '#000',
                                                }}
                                            >
                                                {item.displayName || item.name}
                                            </Typography>

                                            {/* ─────── Duration + Date ─────── */}
                                            {item.duration && item.createdAt && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ marginTop: -1 }}
                                                >
                                                    Created : {formatRelativeDate(item.createdAt)}
                                                </Typography>
                                            )}
                                        </Grid>
                                    ))}
                            </Grid>


                        </>
                    )}
                </List>


            )}

            <VideoDialog
                open={openVideoDialog}
                onClose={() => setOpenVideoDialog(false)}
                videoUrl={selectedVideoUrl}
            />
        </Box>
    );
};

export default StudentVideoDashboard;
